import { duplicatePage } from '../../pages';
import type { Revision } from '../../types';
import { isYValue, mutations, randomUUID, yToJSON } from '../../utils';
import { isRevisionPageParent } from './guards';
import { insertPageAfterInRevision, insertPageInRevision } from './insertPageInRevision';
import { updatePageInRevision } from './updatePageInRevision';

interface DuplicatePageInRevisionArgs {
    /** The id of the original page to duplicate */
    originalPageId: string;
    /** The new id to assign to this page, if any */
    newPageId?: string;
    /** If duplicating in a page group or parent page, identify the parent */
    parentPageId?: string;
}

/**
 * Duplicates an entire page by cloning all its properties.
 *
 * Will duplicate the pages based on their type and also duplicate subpages if any.
 * The pages will be inserted and updated within the revision given in args.
 */
export function duplicatePageInRevision(
    revision: mutations.MutableData<Revision>,
    args: DuplicatePageInRevisionArgs
): string | undefined {
    const { originalPageId, parentPageId } = args;

    const pageId = args.newPageId || randomUUID();
    const originalPage = mutations.getIn(revision, ['pages', originalPageId] as const);

    if (!originalPage) {
        return;
    }

    const clonedPage = duplicatePage(
        isYValue(originalPage) ? yToJSON(originalPage) : originalPage,
        {
            pageId,
            // Only rename the top-level page in a group, leave sub-pages with their original names
            rename: !parentPageId,
        }
    );
    // Insert this page first so we can insert its children properly
    if (parentPageId) {
        insertPageInRevision(revision, clonedPage, {
            into: parentPageId,
        });
    } else {
        insertPageAfterInRevision(revision, clonedPage, originalPageId);
    }

    let pageIds: string[] | undefined;
    // If the page can have subpages
    if (isRevisionPageParent(originalPage)) {
        const subpages = mutations.get(originalPage, 'pages');
        // Clone all subpages now that the parent exists
        pageIds = mutations.map(subpages, (subPageId) => {
            const newSubPageId = randomUUID();

            duplicatePageInRevision(revision, {
                originalPageId: subPageId as string,
                parentPageId: clonedPage.id,
                newPageId: newSubPageId,
            });

            return newSubPageId;
        });
    }

    // Update the inserted page to set the new pages
    updatePageInRevision(revision, clonedPage.id, {
        pages: pageIds,
    });

    return pageId;
}
